import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Feedback";
import { TextField } from "@/components/ui/Field";
import { ApiError } from "@/lib/api";
import { changePassword } from "@/lib/auth-api";

interface Props {
  minLength: number;
  onDone: () => void;
  submitLabel?: string;
}

export function PasswordChangeForm({ minLength, onDone, submitLabel }: Props) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const mutation = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      onDone();
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (next !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    mutation.mutate();
  };

  let error: string | null = null;
  if (mutation.error instanceof ApiError) {
    if (mutation.error.code === "invalid_credentials") error = t("auth.invalidCredentials");
    else if (mutation.error.code === "password_policy") {
      const problems = (mutation.error.detail.problems as string[] | undefined) ?? [];
      error = problems.map((p) => t(`auth.policy.${p}`)).join(" ");
    } else error = t("common.errorGeneric");
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      {error && <Alert>{error}</Alert>}
      <TextField
        label={t("auth.currentPassword")}
        type="password"
        autoComplete="current-password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        required
      />
      <TextField
        label={t("auth.newPassword")}
        type="password"
        autoComplete="new-password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        hint={t("auth.passwordHint", { length: minLength })}
        required
      />
      <TextField
        label={t("auth.confirmPassword")}
        type="password"
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        error={mismatch ? t("auth.passwordsDiffer") : null}
        required
      />
      <Button type="submit" busy={mutation.isPending} className="w-full sm:w-auto">
        {submitLabel ?? t("auth.changePassword")}
      </Button>
    </form>
  );
}
